import {
  FUNNEL_GROUPING_LABELS,
  FUNNEL_GROUPINGS,
  FUNNEL_STAGE_HINTS,
  FUNNEL_STAGE_LABELS,
  FUNNEL_STAGES,
  funnelNotes,
  share,
  type FunnelGrouping,
  type FunnelRow,
} from '@ai-sales/shared'
import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { fetchFunnel } from '../api/reports.api.js'
import { EmptyState } from '../components/data-table.js'
import { ErrorPanel } from '../components/error-panel.js'
import { SelectField } from '../components/field.js'
import { PageHeader, Panel } from '../components/page.js'
import { Spinner } from '../components/spinner.js'
import { useCurrentWorkspace } from '../workspace/workspace-context.js'

/** Готовые периоды. Руками даты вводят редко, а «за месяц» нужно каждый день. */
const PRESETS = [
  { label: '7 дней', days: 7 },
  { label: '30 дней', days: 30 },
  { label: '90 дней', days: 90 },
  { label: 'Всё время', days: 0 },
]

export function FunnelPage() {
  const workspace = useCurrentWorkspace()
  const [days, setDays] = useState(30)
  const [groupBy, setGroupBy] = useState<FunnelGrouping>('subid')

  const from = days > 0 ? new Date(Date.now() - days * 86_400_000).toISOString() : undefined

  const report = useQuery({
    queryKey: ['funnel', workspace.id, days, groupBy],
    queryFn: () => fetchFunnel(workspace.id, { groupBy, ...(from ? { from } : {}) }),
  })

  return (
    <>
      <PageHeader
        title="Воронка"
        description="Где теряются заявки: сколько из них дошло до каждой ступени. Сравнивать источники стоит по доле заказов, а не по количеству заявок"
      />

      <Panel title="Период и разбивка">
        <div className="flex flex-wrap items-end gap-4">
          <div className="flex flex-wrap gap-1.5">
            {PRESETS.map((preset) => (
              <button
                key={preset.label}
                type="button"
                onClick={() => setDays(preset.days)}
                className={
                  preset.days === days
                    ? 'rounded-md border border-accent bg-accent-soft px-3 py-1.5 text-[13px] text-accent'
                    : 'rounded-md border border-line px-3 py-1.5 text-[13px] text-ink-soft hover:bg-paper'
                }
              >
                {preset.label}
              </button>
            ))}
          </div>

          <div className="min-w-56">
            <SelectField
              label="Разбивка"
              value={groupBy}
              onChange={(event) => setGroupBy(event.target.value as FunnelGrouping)}
            >
              {FUNNEL_GROUPINGS.map((value) => (
                <option key={value} value={value}>
                  {FUNNEL_GROUPING_LABELS[value]}
                </option>
              ))}
            </SelectField>
          </div>
        </div>
      </Panel>

      {report.isLoading && (
        <div className="flex items-center gap-2 text-sm text-muted">
          <Spinner /> Считаем…
        </div>
      )}

      {report.error && <ErrorPanel error={report.error} onRetry={() => void report.refetch()} />}

      {report.data && (
        <>
          <Panel
            title="Всего за период"
            description="Доля считается от заявок. Между соседними ступенями её нет, и это не упущение: ступени не вложены одна в другую — агент может передать заявку в колл-центр, не написав клиенту"
          >
            {report.data.total.leads === 0 ? (
              <EmptyState
                title="Заявок за период нет"
                description="Выберите период шире или проверьте, приходят ли сообщения в канал."
              />
            ) : (
              <Stages row={report.data.total} />
            )}
          </Panel>

          {groupBy !== 'none' && report.data.rows.length > 0 && (
            <Panel
              title={FUNNEL_GROUPING_LABELS[groupBy]}
              description="Те же ступени числами. Сравнивать источники стоит по последней колонке, а не по количеству заявок"
            >
              <Breakdown rows={report.data.rows} />
            </Panel>
          )}
        </>
      )}
    </>
  )
}

/**
 * Ступени полосами.
 *
 * Полоса — длина, и только длина: цвет один на все ступени. Разноцветные
 * ступени читались бы как разные величины, хотя это одно и то же число
 * на разных этапах, и разница между ними — единственное, что здесь
 * нужно увидеть.
 *
 * Все значения подписаны прямо на полосе, без наведения: отчёт смотрят
 * и распечатанным, и через плечо.
 */
function Stages({ row }: { row: FunnelRow }) {
  return (
    <ol className="flex flex-col gap-3">
      {FUNNEL_STAGES.map((stage) => {
        const value = row[stage]
        const ofLeads = share(value, row.leads)

        return (
          <li key={stage}>
            <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5">
              <span className="text-sm font-medium text-ink">{FUNNEL_STAGE_LABELS[stage]}</span>
              <span className="text-sm text-ink-soft">
                <span className="font-semibold text-ink">{value}</span>
                {ofLeads !== null && <span className="text-muted"> · {ofLeads}% от заявок</span>}
              </span>
            </div>

            <div className="mt-1 h-2.5 w-full overflow-hidden rounded bg-line-soft">
              <div
                className="h-full rounded bg-accent"
                style={{ width: `${row.leads > 0 ? (value / row.leads) * 100 : 0}%` }}
              />
            </div>

            <p className="mt-1 text-xs text-muted">{FUNNEL_STAGE_HINTS[stage]}</p>
          </li>
        )
      })}

      {funnelNotes(row).map((note) => (
        <li
          key={note}
          className="rounded-md border border-copper/30 bg-copper-soft px-3 py-2 text-xs text-ink-soft"
        >
          {note}
        </li>
      ))}

      <li className="mt-1 border-t border-line pt-3 text-xs text-muted">
        Отказов: <span className="text-ink-soft">{row.refused}</span>. В воронку не входят — это не
        ступень, а ветка в сторону: отказ отличается от молчания, и лечатся они разным.
      </li>
    </ol>
  )
}

function Breakdown({ rows }: { rows: FunnelRow[] }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[46rem] text-sm">
        <thead>
          <tr className="border-b border-line text-left text-xs tracking-wide text-muted uppercase">
            <th className="py-2 pr-3 font-medium">Значение</th>
            {FUNNEL_STAGES.map((stage) => (
              <th key={stage} className="py-2 pr-3 text-right font-medium">
                {FUNNEL_STAGE_LABELS[stage]}
              </th>
            ))}
            <th className="py-2 text-right font-medium">Заказ из заявки</th>
          </tr>
        </thead>

        <tbody>
          {rows.map((row) => {
            const conversion = share(row.ordered, row.leads)

            return (
              <tr key={row.key ?? 'пусто'} className="border-b border-line-soft last:border-0">
                <td className="py-2 pr-3 text-ink">{row.label}</td>
                {FUNNEL_STAGES.map((stage) => (
                  <td key={stage} className="py-2 pr-3 text-right tabular-nums text-ink-soft">
                    {row[stage]}
                  </td>
                ))}
                <td className="py-2 text-right tabular-nums font-medium text-ink">
                  {/* Прочерк, а не 0%: «заявок не было» и «заявки были, заказов нет» — разные новости */}
                  {conversion === null ? '—' : `${conversion}%`}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}
